import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './core/contexts/AuthContext';
import { ToastStore } from './core/hooks/useToast';
import { Toaster } from './components/ui/Toaster';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './features/auth/login/LoginPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { Layout } from './components/layout/Layout';
import { ClientsPage } from './features/clients/ClientsPage';
import { ClientDetailPage } from './features/clients/ClientDetailPage';
import { OrganizationsPage } from './features/organizations/OrganizationsPage';
import { OrganizationDetailPage } from './features/organizations/OrganizationDetailPage';
import { JobsPage } from './features/jobs/JobsPage';
import { DocumentsPage } from './features/documents/DocumentsPage';
import { TemplatesPage } from './features/documents/TemplatesPage';
import { TemplateCreatePage } from './features/documents/TemplateCreatePage';
import { TemplateEditPage } from './features/documents/TemplateEditPage';
import { TemplateMigrationPage } from './features/documents/TemplateMigrationPage';
import { DocumentDetailPage } from './features/documents/DocumentDetailPage';
import { DocumentCreatePage } from './features/documents/DocumentCreatePage';
import { DocumentEditPage } from './features/documents/DocumentEditPage';
import { MarkdownDocumentCreatePage } from './features/documents/MarkdownDocumentCreatePage';
import { TimeTrackingPage } from './features/time-tracking/TimeTrackingPage';
import { InvoicesPage } from './features/invoices/InvoicesPage';
import { InvoiceCreatePage } from './features/invoices/InvoiceCreatePage';
import { InvoiceDetailPage } from './features/invoices/InvoiceDetailPage';
import { SupportTicketsPage } from './features/support/SupportTicketsPage';
import { JobsKanbanPage } from './features/jobs/JobsKanbanPage';
import { TasksPage } from './features/tasks/TasksPage';
import { TasksKanbanPage } from './features/tasks/TasksKanbanPage';
import { ClientRequestsPage } from './features/client-requests/ClientRequestsPage';
import { DesignSystemPage } from './features/design-system/DesignSystemPage';
import { ServicesPage } from './features/services/ServicesPage';
import { UsersPage } from './features/users/UsersPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { SettingsPage } from './features/settings/SettingsPage';
import { HelpPage } from './features/help/HelpPage';
import { EnvelopesPage } from './features/signatures/EnvelopesPage';
import { EnvelopeCreatePage } from './features/signatures/EnvelopeCreatePage';
import { EnvelopeDetailPage } from './features/signatures/EnvelopeDetailPage';
import { CompanySigningPage } from './features/signatures/CompanySigningPage';
import { ClientSigningPage } from './features/signatures/ClientSigningPage';
import { DocumentSigningPage } from './features/documents/DocumentSigningPage';

function App() {
  return (
    <AuthProvider>
      <ToastStore>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Public signing routes (no authentication required) */}
        <Route path="/sign/:token" element={<ClientSigningPage />} />
        <Route path="/document/sign/:token" element={<DocumentSigningPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Clients */}
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:clientId" element={<ClientDetailPage />} />

          {/* Organizations */}
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:organizationId" element={<OrganizationDetailPage />} />

          {/* Jobs */}
          <Route path="jobs" element={<JobsPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/kanban" element={<TasksKanbanPage />} />
          <Route path="client-requests" element={<ClientRequestsPage />} />

          {/* Time Tracking */}
          <Route path="time-tracking" element={<TimeTrackingPage />} />

          {/* Invoices */}
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/new" element={<InvoiceCreatePage />} />
          <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />

          {/* Support */}
          <Route path="support" element={<SupportTicketsPage />} />

          {/* Jobs Kanban */}
          <Route path="jobs/kanban" element={<JobsKanbanPage />} />

          {/* Design System */}
          <Route path="design-system" element={<DesignSystemPage />} />

          {/* Documents */}
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="documents/new" element={<DocumentCreatePage />} />
          <Route path="documents/new/markdown" element={<MarkdownDocumentCreatePage />} />
          <Route path="documents/:documentId/edit" element={<DocumentEditPage />} />
          <Route path="documents/:documentId" element={<DocumentDetailPage />} />
          <Route path="documents/templates" element={<TemplatesPage />} />
          <Route path="documents/templates/new" element={<TemplateCreatePage />} />
          <Route path="documents/templates/migrate" element={<TemplateMigrationPage />} />
          <Route path="documents/templates/:templateId/edit" element={<TemplateEditPage />} />

          {/* Services */}
          <Route path="services" element={<ServicesPage />} />

          {/* Users */}
          <Route path="users" element={<UsersPage />} />

          {/* Profile */}
          <Route path="profile" element={<ProfilePage />} />

          {/* Settings */}
          <Route path="settings" element={<SettingsPage />} />

          {/* Help */}
          <Route path="help" element={<HelpPage />} />

          {/* Signature Envelopes */}
          <Route path="envelopes" element={<EnvelopesPage />} />
          <Route path="envelopes/new" element={<EnvelopeCreatePage />} />
          <Route path="envelopes/:id" element={<EnvelopeDetailPage />} />
          <Route path="envelopes/:id/sign" element={<CompanySigningPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster />
      </ToastStore>
    </AuthProvider>
  );
}

export default App;
